import React from 'react'
import { shape, string, array, func } from 'prop-types'
import { createComponent, withTheme } from '@ycos/fela'
import { Analytics } from '@ycos/analytics'
import { createFontStyle, getFontStyles } from '@ycos/primitives'
import { BackgroundPicture } from '@ycos/picture'

const Container = createComponent(
  'Container',
  ({ theme }) => ({
    alignItems: 'center',
    cursor: 'pointer',
    display: 'flex',
    height: '100%',
    textDecoration: 'none',
    justifyContent: 'center',
    'screen-large': {
      display: 'block',
      padding: `0 ${3 * theme.spacingMultiplier}px`
    }
  }),
  'a'
)

const Wrapper = createComponent(
  'Wrapper',
  ({ theme, brand }) => ({
    padding: brand?.site === 'theoutnet' ? `0 ${4 * theme.spacingMultiplier}px` : 'initial'
  })
)

const CategoriesCarouselWrapper = createComponent('CategoriesCarouselWrapper', ({ theme }) => ({
  padding: `${4 * theme.spacingMultiplier}px 0`,
  'screen-large': {
    padding: `${6 * theme.spacingMultiplier}px 0 ${5 * theme.spacingMultiplier}px`,
    color: theme.typography.colors.PrimaryWhite
  }
}))

const CategoriesWrapper = createComponent('CategoriesWrapper', ({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  overflowX: 'scroll',
  gap: `${2 * theme.spacingMultiplier}px`,
  scrollbarWidth: 'none',
  '::-webkit-scrollbar': {
    display: 'none'
  },
  'screen-medium': {
    justifyContent: 'center',
    overflowX: 'initial'
  }
}))

const Category = createComponent(
  'Category',
  ({ theme, brand }) => {
    const fontStyle = getFontStyles(theme.typography, { type: 'Label', name: '1' })
    return {
      ...fontStyle,
      textDecoration: 'none',
      padding: `${brand === 'theoutnet' ? 9 : 11}px ${2 * theme.spacingMultiplier}px `,
      border: `1px solid ${theme.typography.colors.PrimaryWhite}`,
      borderRadius: '2px',
      color: theme.typography.colors.PrimaryWhite,
      flexShrink: 0,
      fontWeight: brand === 'theoutnet' ? '400' : 'initial',
      ':hover': {
        color: theme.typography.colors.PrimaryBlack,
        background: theme.typography.colors.PrimaryWhite
      },
      ':last-of-type': {
        marginRight: `${2 * theme.spacingMultiplier}px`
      }
    }
  },
  'a'
)

const SingleColumn = createComponent('SingleColumn', ({ brand, theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: `${3 * theme.spacingMultiplier}px`,
  color: theme.typography.colors.PrimaryWhite,
  height: brand?.site === 'theoutnet' ? '100%' : '100px',
  'screen-large': {
    height: '135px'
  }
}));

const CenterSaleTitle = createComponent(
  'CenterSaleTitle',
  ({ theme }) => ({
    color: theme.typography.colors.PrimaryWhite,
    textTransform: 'uppercase',
    fontSize: '32px',
    lineHeight: '40px',
    margin: 0,
    fontWeight: 400,
    ...theme.custom.saleBanner.title,
    'screen-large': {
      fontSize: '48px',
      lineHeight: '60px'
    }
  }),
  'h2'
)

const CenterDiscountText = createComponent('CenterDiscountText', ({ theme }) => ({
  display: 'flex',
  gap: `${theme.spacingMultiplier}px`,
  width: '100%',
  alignItems: 'baseline',
  justifyContent: 'center'
}), 'div')

const CenterDiscountTitle = createFontStyle('CenterDiscountTitle', { type: 'Paragraph', tagName: 'div', name: '3' }, ({ theme }) => ({
  fontSize: '32px',
  lineHeight: '32px',
  fontWeight: '400',
  color: theme.typography.colors.PrimaryWhite,
  'screen-large': {
    fontSize: '40px',
    lineHeight: '40px'
  }
}))

const CenterDiscountPercentage = createFontStyle('CenterDiscountPercentage', { type: 'Paragraph', tagName: 'div', name: '3' }, ({ theme }) => ({
  display: 'inline-block',
  fontSize: '48px',
  lineHeight: '60px',
  fontWeight: '700',
  textTransform: 'uppercase',
  color: theme.typography.colors.PrimaryWhite,
  'screen-large': {
    fontSize: '64px',
    lineHeight: 'normal'
  }
}))

const DividerWrapper = createComponent('DividerWrapper', ({ theme }) => ({
  paddingRight: `${2 * theme.spacingMultiplier}px`,
  'screen-large': {
    paddingRight: 'initial'
  }
}
))

const SmallDivider = createComponent('SmallDivider', ({ theme }) => {
  const fontStyle = getFontStyles(theme.typography, { type: 'Paragraph', name: '4' })
  return {
    ...fontStyle,
    overflow: 'hidden',
    textAlign: 'center',
    color: theme.typography.colors.PrimaryWhite,
    '&::before': {
      right: '0.5em',
      marginLeft: '-50%',
      backgroundColor: theme.typography.colors.SecondaryGrey,
      content: "''",
      display: 'inline-block',
      height: '1px',
      position: 'relative',
      verticalAlign: 'middle',
      width: '50%'
    },
    '&::after': {
      left: '0.5em',
      marginRight: '-50%',
      backgroundColor: theme.typography.colors.SecondaryGrey,
      content: "''",
      display: 'inline-block',
      height: '1px',
      position: 'relative',
      verticalAlign: 'middle',
      width: '50%'
    },
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '16px',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    'screen-large': {
      fontSize: '24px',
      lineHeight: '25px',
      '&::before': {
        right: `${10 * theme.spacingMultiplier}px`
      },
      '&::after': {
        left: `${10 * theme.spacingMultiplier}px`
      }
    }
  }
});

/* AB Sale Banner Component for TON */
/* MRP, NAP, and TON use different components for sale banner */
const SaleBannerWithCategoriesTON = (props) => {
  const { item, config = {}, brand, theme, locale } = props
  /* First item => Sale Banner teaser with target */
  /* Second item => Categories carousel */
  const { subTitlePlain, preTitlePlain, teaserTitle, teaserTextPlain, pictures, dataCmMetadata, fetchPriority, target } = item?.items?.[0]
  const { imageSphere } = config

  const contentContainerStyles = theme.custom.saleBanner.contentContainerStyles
  const additionalStyles = theme.custom.saleBanner.additionalBackgroundPictureStyles

  // if target is augmented category -> categoryLink
  // if target is external link -> href
  const bannerHref = target?.categoryLink || target?.href

  return (
    <Wrapper brand={brand}>
      <BackgroundPicture
        fetchPriority={fetchPriority}
        opacity={1}
        images={pictures}
        height="100%"
        opacityColor="transparent"
        additionalStyles={additionalStyles}
        imagesphere={imageSphere}
        lazy={false}
        contentContainerStyles={contentContainerStyles}
      >
        <Container data-cm-metadata={dataCmMetadata} data-testid="saleBannerLink" href={bannerHref} onClick={track({ item: item?.items?.[0], brand, destinationUrl: bannerHref })}>
          <SingleColumn brand={brand}>
            <CenterSaleTitle>{teaserTitle}</CenterSaleTitle>
            <CenterDiscountText>
              <CenterDiscountTitle brand={brand}>{preTitlePlain}</CenterDiscountTitle>
              <CenterDiscountPercentage>{subTitlePlain}</CenterDiscountPercentage>
            </CenterDiscountText>
          </SingleColumn>
        </Container>
        <DividerWrapper>
          <SmallDivider>{teaserTextPlain}</SmallDivider>
        </DividerWrapper>
        <CategoriesCarouselWrapper>
          <CategoriesWrapper>
            {item?.items?.[1]?.items?.map(category => {
              const { categoryLink } = category.target || {}
              const ynapParam = category.ynapParameter?.toLowerCase()
              const finalAugmentedCategoryUrl = categoryLink && ynapParam
                ? `${categoryLink.replace(/\/$/, '')}/${ynapParam}`
                : categoryLink
              const externalLink = category.href
              const localizedHref = externalLink?.startsWith('https://www.theoutnet.com')
                ? externalLink.replace('https://www.theoutnet.com', `https://www.theoutnet.com/${locale?.id}`)
                : externalLink

              const finalHref = finalAugmentedCategoryUrl || localizedHref
              return (
                <Category brand={brand} href={finalHref} key={`category-${category.teaserTitle}`} onClick={track({ item: category, brand, destinationUrl: finalHref })}>
                  {category.teaserTitle}
                </Category>
              )
            })}
          </CategoriesWrapper>
        </CategoriesCarouselWrapper>
      </BackgroundPicture>
    </Wrapper>
  )
}

SaleBannerWithCategoriesTON.layoutVariant = ['isc-sale-banner-with-categories-ton']

SaleBannerWithCategoriesTON.propTypes = {
  item: shape({
    preTitlePlain: string.isRequired,
    subTitlePlain: string,
    teaserTitle: string.isRequired,
    teaserTextPlain: string.isRequired,
    pictures: array,
    layoutVariant: string.isRequired,
    target: shape({
      href: string.isRequired,
      teaserTitle: string.isRequired,
      teaserTextPlain: string.isRequired
    }).isRequired
  }),
  onTargetClick: func.isRequired
}

SaleBannerWithCategoriesTON.defaultProps = {
  item: {}
}

export default withTheme(SaleBannerWithCategoriesTON)

const getSiteMerchObject = (item, destinationUrl) => {
  const { type, contentID, layoutVariant, teaserTitle, rootPosition } = item
  if (type !== 'ynapteaser') return

  return {
    contentId: contentID,
    componentType: layoutVariant,
    title: teaserTitle,
    destinationUrl: destinationUrl,
    position: rootPosition
  }
}

const track = ({ item, brand, destinationUrl }) => {
  if (!item.href) return undefined

  const siteMerchandising = getSiteMerchObject(item, destinationUrl)
  if (siteMerchandising) {
    const analytics = new Analytics({ brand: brand.brandId })
    return () => {
      analytics.trackEvent({
        event: 'site merchandising - click',
        effect: 'campaign click',
        category: {
          primaryCategory: 'site merchandising',
          subCategory: 'homepage'
        },
        attributes: {
          siteMerchandising: [siteMerchandising]
        }
      })
    }
  }
}